<template>
  <div class="page-container">
    <div ref="dragZoomContainer" class="drag-zoom-container" @mousemove="mouseMove" @mouseup="mouseUp" @mousewheel="mouseWheel">
      <div ref="dragContent" class="drag-content" :style="{...dragContentStyle, 'cursor': dragOption.canDrag ? 'grabbing':'grab'}"  @mousedown="mouseDown">
        <div ref="zoomContent" class="zoom-content" :style="zoomContentStyle">
          <slot>
            <div class="tip-info">请将需要拖拽和缩放的元素，通过默认插槽写入该容器中！</div>
          </slot>
        </div>
      </div>
    </div>
    <div class="operate-container" v-if="showAction">
      <el-button type="primary">放 大</el-button>
      <el-button type="primary">移 动</el-button>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, reactive, ref, watch } from 'vue'

const SCALE_MIN = 0.2
const SCALE_MAX = 3

const dragZoomContainer = ref(null);
const dragContent = ref(null);
const zoomContent = ref(null);
const dragContentStyle = reactive({});
const zoomContentStyle = reactive({});
const touchActive = ref(false);

const props = defineProps({
  showAction: {
    type: Boolean,
    default: false,
  },
  /** 变化时重置缩放与位移（如切换电站 id） */
  resetKey: {
    type: [String, Number],
    default: undefined,
  },
})

watch(() => props.resetKey, (val, prev) => {
  if (prev !== undefined && val !== prev) {
    resetTransform()
  }
})


/** 拖拽的控制参数 */
const dragOption = reactive({
  originLeft: 0,
  originTop: 0,
  originClientX: 0,
  originClientY: 0,
  canDrag: false,
})


/** 缩放的控制参数 */
const scaleOption = reactive({
  currentScale: 1,
  originScale: 1,
})

/** 双指捏合缩放的控制参数 */
const pinchOption = reactive({
  active: false,
  initialDistance: 0,
  initialScale: 1,
  anchorRatioX: 0.5,
  anchorRatioY: 0.5,
  baseWidth: 0,
  baseHeight: 0,
})

const onDocumentMouseUp = () => {
  if (dragOption.canDrag) {
    initOptions();
  }
}

const touchStart = (e) => {
  touchActive.value = true
  if (e.touches.length === 2) {
    e.preventDefault()
    initPinch(e.touches)
    return
  }
  if (e.touches.length === 1 && !pinchOption.active && dragContent.value?.contains(e.target)) {
    e.preventDefault()
    initOperateOnPointerDown(e.touches[0])
  }
}

const touchMove = (e) => {
  if (e.touches.length === 2) {
    e.preventDefault()
    if (!pinchOption.active) {
      initPinch(e.touches)
    }
    pinchDom(e.touches)
    return
  }
  if (e.touches.length === 1 && dragOption.canDrag && !pinchOption.active) {
    e.preventDefault()
    dragDom(e.touches[0])
  }
}

const touchEnd = (e) => {
  if (e.touches.length < 2) {
    pinchOption.active = false
    pinchOption.initialDistance = 0
  }
  if (e.touches.length === 0) {
    dragOption.canDrag = false
    window.setTimeout(() => {
      touchActive.value = false
    }, 0)
  } else if (e.touches.length === 1 && !pinchOption.active) {
    initOperateOnPointerDown(e.touches[0])
  }
}

onMounted(() => {
  document.addEventListener('mouseup', onDocumentMouseUp)

  const el = dragZoomContainer.value
  el.addEventListener('touchstart', touchStart, { passive: false })
  el.addEventListener('touchmove', touchMove, { passive: false })
  el.addEventListener('touchend', touchEnd)
  el.addEventListener('touchcancel', touchEnd)
})

onUnmounted(() => {
  document.removeEventListener('mouseup', onDocumentMouseUp)

  const el = dragZoomContainer.value
  if (el) {
    el.removeEventListener('touchstart', touchStart)
    el.removeEventListener('touchmove', touchMove)
    el.removeEventListener('touchend', touchEnd)
    el.removeEventListener('touchcancel', touchEnd)
  }
})


const mouseDown = function (e) {
  if (touchActive.value) return
  e.stopPropagation(); // 阻止事件冒泡 >>> 避免按下时选中图标或文字
  e.preventDefault(); // 阻止默认行为
  initOperateOnPointerDown(e);
}

const mouseMove = function (e) {
  if (dragOption.canDrag) {
    dragDom(e);
  }
}

const mouseUp = function (e) {
  dragOption.canDrag = false;
}

const mouseWheel = function (e) {
  e.preventDefault();
  changeDomScaleByWhell(e, zoomContent.value);
}




/** ======[辅助-功能方法]============================================================================================= */
/**
 * 初始化配置
 * 使用场景：鼠标拖拽过程中，移出了拖拽区域后抬起，重新init配置项
 */
function initOptions() {
  dragOption.canDrag = false;
}

/** 重置缩放与位移到初始状态 */
function resetTransform() {
  initOptions()
  touchActive.value = false

  scaleOption.currentScale = 1
  scaleOption.originScale = 1

  pinchOption.active = false
  pinchOption.initialDistance = 0
  pinchOption.initialScale = 1

  Object.keys(dragContentStyle).forEach((key) => {
    delete dragContentStyle[key]
  })

  if (zoomContent.value) {
    zoomContent.value.style.transform = ''
  }
}

defineExpose({ resetTransform })


/** 指针按下时，初始化拖拽参数（鼠标 / 触摸） */
function initOperateOnPointerDown(pointer) {
  const containerRect = dragZoomContainer.value.getBoundingClientRect();
  const contentRect = dragContent.value.getBoundingClientRect();

  // 根据缩放比例，计算原始的left-top的距离
  dragOption.originLeft = contentRect.left - containerRect.left;
  dragOption.originTop = contentRect.top - containerRect.top;

  // 记录拖拽开始时，指针在可视区域的坐标
  dragOption.originClientX = pointer.clientX;
  dragOption.originClientY = pointer.clientY;
  
  // 设置拖拽的控制参数
  dragOption.canDrag = true;
}


/** 执行拖拽 */
function dragDom(pointer) {
  // 计算指针移动的delta量：根据 【当前指针在可视区域的坐标】 减去 【拖拽开始时指针在可视区域的坐标】 
  const deltaLeft = pointer.clientX - dragOption.originClientX;
  const deltaTop = pointer.clientY - dragOption.originClientY;

  // 更新被拖拽元素的定位参数 left-top
  const left = dragOption.originLeft + deltaLeft;
  const top = dragOption.originTop + deltaTop;
  dragContentStyle.left = left + 'px';
  dragContentStyle.top = top + 'px';
}

function getTouchDistance(touches) {
  const dx = touches[0].clientX - touches[1].clientX
  const dy = touches[0].clientY - touches[1].clientY
  return Math.hypot(dx, dy)
}

function getTouchCenter(touches) {
  return {
    clientX: (touches[0].clientX + touches[1].clientX) / 2,
    clientY: (touches[0].clientY + touches[1].clientY) / 2,
  }
}

function syncCurrentScaleFromDom(dom) {
  const res = dom.style.transform.match(/scale\(([\d.]+)/)
  if (Array.isArray(res) && res.length > 1) {
    scaleOption.currentScale = parseFloat(res[1])
  }
}

function applyDomScale(dom, newScale, pointer, min = SCALE_MIN, max = SCALE_MAX) {
  const clampedScale = Math.min(max, Math.max(min, newScale))
  if (clampedScale === scaleOption.currentScale) {
    dom.style.transform = 'scale(' + clampedScale.toFixed(2) + ')'
    return
  }
  scaleOption.originScale = scaleOption.currentScale
  scaleOption.currentScale = clampedScale
  dom.style.transform = 'scale(' + clampedScale.toFixed(2) + ')'
  associatedWithDragContainerForZooming(pointer)
}

function initPinch(touches) {
  initOptions()
  pinchOption.active = true
  pinchOption.initialDistance = getTouchDistance(touches)
  syncCurrentScaleFromDom(zoomContent.value)
  pinchOption.initialScale = scaleOption.currentScale

  const dragContentRect = dragContent.value.getBoundingClientRect()
  const center = getTouchCenter(touches)

  pinchOption.baseWidth = dragContentRect.width / pinchOption.initialScale
  pinchOption.baseHeight = dragContentRect.height / pinchOption.initialScale
  pinchOption.anchorRatioX = (center.clientX - dragContentRect.left) / dragContentRect.width
  pinchOption.anchorRatioY = (center.clientY - dragContentRect.top) / dragContentRect.height
}

/** 双指捏合缩放：基于起始锚点绝对计算，避免每帧读布局导致抖动 */
function pinchDom(touches) {
  if (!pinchOption.initialDistance) return

  const distance = getTouchDistance(touches)
  const newScale = Math.min(
    SCALE_MAX,
    Math.max(SCALE_MIN, pinchOption.initialScale * (distance / pinchOption.initialDistance))
  )
  const center = getTouchCenter(touches)
  const containerRect = dragZoomContainer.value.getBoundingClientRect()
  const newWidth = pinchOption.baseWidth * newScale
  const newHeight = pinchOption.baseHeight * newScale

  scaleOption.currentScale = newScale
  scaleOption.originScale = newScale
  zoomContent.value.style.transform = `scale(${newScale})`
  dragContentStyle.width = newWidth + 'px'
  dragContentStyle.height = newHeight + 'px'
  dragContentStyle.left = (center.clientX - containerRect.left - pinchOption.anchorRatioX * newWidth) + 'px'
  dragContentStyle.top = (center.clientY - containerRect.top - pinchOption.anchorRatioY * newHeight) + 'px'
}

/** 鼠标滚轮-增减dom的scale */
function changeDomScaleByWhell(e, dom, min = SCALE_MIN, max = SCALE_MAX) {
  syncCurrentScaleFromDom(dom)
  const delta =  e.deltaY < 0 ? 0.1 : -0.1; // 滚轮外滚-放大, 滚轮内滚-缩小
  if ((scaleOption.currentScale == min && delta < 0) || (scaleOption.currentScale == max && delta > 0)) {
    // 缩放边界值处理：达到[min,max]边界时，不执行缩放
    dom.style.transform = 'scale(' + scaleOption.currentScale.toFixed(1) + ')';
  } else {
    applyDomScale(dom, scaleOption.currentScale + delta, e, min, max)
  }
}


/**
 * 关联DOM：dragContent & zoomContent
 * 控制1：缩放过程中【dragContent的size始终】和【zoomContent缩放之后的size】保持一致，视觉上二者是一体的
 * 控制2：设置缩放中心。默认以鼠标位置为缩放中心，鼠标不在画布上时-以画布中心为缩放中心
 */
function associatedWithDragContainerForZooming(e, isFollowMouse = true) {
  const containerRect = dragZoomContainer.value.getBoundingClientRect();
  const dragContentRect = dragContent.value.getBoundingClientRect();
  const zoomContentRect = zoomContent.value.getBoundingClientRect();

  /** 控制：缩放过程中【dragContent的size始终】和【zoomContent缩放之后的size】保持一致，视觉上二者是一体的 */
  dragContentStyle.width = zoomContentRect.width + 'px';
  dragContentStyle.height = zoomContentRect.height + 'px';

  /** 控制：设置缩放中心 */
  let ratioX = 1 / 2; // 默认以画布中心位置为缩放中心，缩放画布
  let ratioY = 1 / 2; // 默认以画布中心位置为缩放中心，缩放画布
  const isMouseInDragDom = (e.clientX >= dragContentRect.left) && (e.clientX <= dragContentRect.left + dragContentRect.width) 
                        && (e.clientY >= dragContentRect.top) && (e.clientY <= dragContentRect.top + dragContentRect.height); // 如果鼠标不在画布上wheel，则以画布中心为缩放中线 
  
  if (isFollowMouse && isMouseInDragDom) {
    /** 设置以鼠标所在位置为中心进行缩放：通过重新计算【dragContent的left-top来实现】
     * 1、计算鼠标距离 dragContent-left 的比例 >>> ratioX
     * 2、计算鼠标距离 dragContent-top 的比例 >>> ratioY
     */
    ratioX = (e.clientX - dragContentRect.left) / dragContentRect.width;
    ratioY = (e.clientY - dragContentRect.top) / dragContentRect.height;
  }

  // 根据缩放中心，计算left-top的delta值
  const deltaLeft = (scaleOption.currentScale - scaleOption.originScale) * zoomContentRect.width * ratioX;
  const deltaTop = (scaleOption.currentScale - scaleOption.originScale) * zoomContentRect.height * ratioY;

  // 更新dragContent的定位的left-top的值
  dragContentStyle.left = dragContentRect.left - containerRect.left - deltaLeft + 'px';
  dragContentStyle.top = dragContentRect.top- containerRect.top - deltaTop  + 'px';
}

</script>

<style lang="scss" scoped>
.page-container {
  position: relative;
  width: 100%;
  height: 100%;


  .drag-zoom-container {
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    overflow: hidden; // 内部元素移除容器的部分，隐藏
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    touch-action: none; // 禁用浏览器默认手势，由组件自行处理拖拽与捏合缩放

    .drag-content {
      width: fit-content;
      height: fit-content;
      position: absolute;
      cursor: grab;
      user-select: none; // 禁止对内容进行选择
      overflow: hidden;
      display: flex;
      justify-content: center;
      align-items: center;

      .zoom-content {
        width: fit-content;
        height: fit-content;
        padding: 30px;
      }
    }

  }

  .operate-container {
    position: absolute;
    z-index: 1;
  }
}

</style>
